/** Server-mediated live assist media: PCM in (16 kHz int16 mono) + PCM or MP3 TTS out. */

import { getSharedSileroBargeVad, warmSileroBargeVad } from './bargeInVad';
import { PcmPlaybackPlayer, type PcmPlaybackStats } from './pcmPlaybackPlayer';

export const MEDIA_PCM_SAMPLE_RATE = 16000;
/** v3 streaming requires 50–1000 ms frames; batch to 100 ms of int16 mono PCM. */
export const MEDIA_PCM_BATCH_BYTES = 3200;
export const MEDIA_PCM_MIN_BYTES = 1600;

/** Client-side barge-in / STT gating (defaults match server config). */
export interface MediaBargeInConfig {
  barge_in_enabled: boolean;
  barge_in_grace_ms: number;
  barge_in_rms_threshold: number;
  barge_in_min_frames: number;
  barge_in_vad_enabled: boolean;
  barge_in_vad_threshold: number;
  stt_gate_while_bot_speaking: boolean;
}

export interface MediaTtsWireConfig {
  tts_playback: 'pcm' | 'mp3';
  tts_encoding: 'pcm_s16le' | 'mp3';
  tts_sample_rate: number | null;
  tts_channels: number;
  tts_preroll_ms: number;
}

export const DEFAULT_TTS_WIRE_CONFIG: MediaTtsWireConfig = {
  tts_playback: 'pcm',
  tts_encoding: 'pcm_s16le',
  tts_sample_rate: 16000,
  tts_channels: 1,
  tts_preroll_ms: 80,
};

/** True when this websocket binary should be fed to the PCM player. */
export function shouldPushAgentPcm(
  encoding: MediaTtsWireConfig['tts_encoding'],
  receivingTts: boolean,
  turnAborted: boolean
): boolean {
  return encoding === 'pcm_s16le' && receivingTts && !turnAborted;
}

export const DEFAULT_MEDIA_BARGE_IN_CONFIG: MediaBargeInConfig = {
  barge_in_enabled: true,
  barge_in_grace_ms: 1200,
  barge_in_rms_threshold: 0.04,
  barge_in_min_frames: 1,
  barge_in_vad_enabled: true,
  barge_in_vad_threshold: 0.5,
  stt_gate_while_bot_speaking: true,
};

const BARGE_IN_MIC_DEBOUNCE_MS = 450;
/** Mic audio kept while STT is gated, then sent whole when the gate opens. */
const STT_PREROLL_MS = 2000;
const STT_PREROLL_BYTES = Math.floor((MEDIA_PCM_SAMPLE_RATE * 2 * STT_PREROLL_MS) / 1000);

export interface MediaWsTurnComplete {
  turn: number;
  speaker: string;
  utterance: string;
  result: Record<string, unknown>;
}

export interface MediaWsCallbacks {
  onReady?: () => void;
  onPartialStt?: (text: string) => void;
  onSttFinal?: (text: string, speaker: string) => void;
  onBotSpeech?: (turn: number | null, text: string) => void;
  onTtsEnd?: (turn: number | null) => void;
  onTurnComplete?: (payload: MediaWsTurnComplete) => void;
  onError?: (message: string) => void;
  onClose?: () => void;
  /** Soft STT transport status (reconnect); not a fatal session error. */
  onSttStatus?: (state: string, payload: Record<string, unknown>) => void;
  /** Fired once when the first PCM batch is sent to the server (confirms mic capture). */
  onPcmStreaming?: () => void;
}

export function buildMediaWebSocketUrl(apiBaseUrl: string, mediaPath: string, token: string): string {
  const trimmedPath = mediaPath.startsWith('/') ? mediaPath : `/${mediaPath}`;
  const base = apiBaseUrl.replace(/\/$/, '');
  const wsBase = base.startsWith('https://')
    ? base.replace(/^https:\/\//, 'wss://')
    : base.replace(/^http:\/\//, 'ws://');
  const url = new URL(`${wsBase}${trimmedPath}`);
  url.searchParams.set('token', token);
  return url.toString();
}

export function downsampleFloat32(buffer: Float32Array, inputRate: number, outputRate: number): Float32Array {
  if (inputRate === outputRate || buffer.length === 0) return buffer;
  const ratio = inputRate / outputRate;
  const outLen = Math.max(1, Math.round(buffer.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    out[i] = buffer[Math.min(Math.floor(i * ratio), buffer.length - 1)];
  }
  return out;
}

export function float32ToInt16Pcm(buffer: Float32Array): ArrayBuffer {
  const out = new ArrayBuffer(buffer.length * 2);
  const view = new DataView(out);
  for (let i = 0; i < buffer.length; i += 1) {
    const s = Math.max(-1, Math.min(1, buffer[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return out;
}

const MIC_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const SILENT_WAV =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAA=';

export type SessionAudio = {
  captureContext: AudioContext;
  micPromise: Promise<MediaStream>;
  micStream: MediaStream | null;
};

/**
 * Start every gesture-sensitive browser audio call before the first await.
 * Do not await AudioContext.resume() — Safari may leave it pending until the
 * tab is visible again. getUserMedia still runs; await that separately.
 */
export function beginSessionAudioFromGesture(): SessionAudio {
  const micPromise = navigator.mediaDevices.getUserMedia({ audio: MIC_CONSTRAINTS });
  const captureContext = new AudioContext();
  void captureContext.resume();
  const silent = new Audio(SILENT_WAV);
  silent.volume = 0.01;
  void silent.play().catch(() => undefined);
  try {
    const buffer = captureContext.createBuffer(1, 1, captureContext.sampleRate);
    const source = captureContext.createBufferSource();
    source.buffer = buffer;
    source.connect(captureContext.destination);
    source.start(0);
  } catch {
    /* ignore */
  }
  return { captureContext, micPromise, micStream: null };
}

/** Recovery only: Safari often resumes a suspended context on pageshow / visible. */
export function bindAudioContextResumeRecovery(ctx: AudioContext): () => void {
  const kick = () => {
    if (ctx.state === 'suspended') void ctx.resume();
  };
  const onVisibility = () => {
    if (document.visibilityState === 'visible') kick();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', kick);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', kick);
  };
}

export function closeSessionAudio(audio: SessionAudio | null): void {
  if (!audio) return;
  audio.micStream?.getTracks().forEach((track) => track.stop());
  audio.micPromise.then(
    (stream) => stream.getTracks().forEach((track) => track.stop()),
    () => undefined
  );
  audio.captureContext.close().catch(() => undefined);
}

/** Unlock autoplay on a context created during a user gesture. */
export async function unlockAudioContext(ctx: AudioContext): Promise<void> {
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
  const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start(0);
}

export class LiveAssistMediaClient {
  private ws: WebSocket | null = null;
  /** Capture + bot TTS playback (MediaElement routed through context for AEC reference). */
  private captureContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private keepAliveOsc: OscillatorNode | null = null;
  private micStream: MediaStream | null = null;
  private pendingBinary: Uint8Array[] = [];
  private receivingTts = false;
  private ttsCancelled = false;
  private closed = false;
  private pcmSendBuffer = new Uint8Array(0);
  private micStarted = false;
  private micMuted = false;
  private speakerMuted = false;
  private playbackGain: GainNode | null = null;
  private pcmSent = false;
  private pcmFlushTimer: ReturnType<typeof setInterval> | null = null;
  private lastBargeSentAt = 0;
  private botSpeechStartedAt = 0;
  private bargeLoudFrames = 0;
  /** Start of the current RMS-qualified stretch, for barge-in timing logs. */
  private bargeCandidateAt = 0;
  private vadBusy = false;
  private vadPending: Float32Array | null = null;
  private sttPreroll: Uint8Array[] = [];
  private sttPrerollBytes = 0;
  /** False for this call when the shared Silero warmup failed. */
  private bargeVadAvailable = true;
  private readonly bargeVad = getSharedSileroBargeVad();
  private bargeInConfig: MediaBargeInConfig = { ...DEFAULT_MEDIA_BARGE_IN_CONFIG };
  private ttsWire: MediaTtsWireConfig = { ...DEFAULT_TTS_WIRE_CONFIG };
  private pcmPlayer: PcmPlaybackPlayer | null = null;
  /** After fatal_overflow, drop PCM until the next bot_speech. */
  private pcmTurnAborted = false;
  private unbindResumeRecovery: (() => void) | null = null;
  /** Current agent turn being streamed/played. */
  private botTurn: number | null = null;
  private playbackReportSent = false;
  /** MSE incremental MP3 playback. */
  private streamAudio: HTMLAudioElement | null = null;
  private streamElementSource: MediaElementAudioSourceNode | null = null;
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private streamObjectUrl: string | null = null;
  private streamAppendQueue: Uint8Array[] = [];
  private streamSourceOpen = false;
  private streamEndRequested = false;
  /** iOS WebKit (Safari and Chrome on iPhone) has no MediaSource audio/mpeg. */
  private readonly blobPlayback = !(
    typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg')
  );

  constructor(private readonly callbacks: MediaWsCallbacks = {}) {}

  /** Call during the user-gesture handler (Create session click). */
  attachSessionAudio(captureContext: AudioContext, micStream: MediaStream): void {
    this.captureContext = captureContext;
    this.micStream = micStream;
    this.unbindResumeRecovery?.();
    this.unbindResumeRecovery = bindAudioContextResumeRecovery(captureContext);
    void captureContext.resume();
    this.ensurePcmPlayer();
  }

  /** Kick resume without waiting; Safari may not resolve until the tab is visible. */
  private kickResume(): void {
    const ctx = this.captureContext;
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }

  private ensurePcmPlayer(): void {
    if (this.pcmPlayer) return;
    if (this.ttsWire.tts_encoding !== 'pcm_s16le') return;
    if (!this.captureContext) return;
    const rate = this.ttsWire.tts_sample_rate || MEDIA_PCM_SAMPLE_RATE;
    this.pcmPlayer = new PcmPlaybackPlayer(
      this.captureContext,
      {
        onEnded: (stats) => this.onPcmPlaybackEnded(stats, false),
        onFatalOverflow: (stats) => this.onPcmFatalOverflow(stats),
        onError: (message) => this.callbacks.onError?.(message),
      },
      rate
    );
    this.pcmPlayer.setPrerollMs(this.ttsWire.tts_preroll_ms);
    this.pcmPlayer.setMuted(this.speakerMuted);
    void this.pcmPlayer.ensureNode();
  }

  setMicMuted(muted: boolean): void {
    this.micMuted = muted;
  }

  setSpeakerMuted(muted: boolean): void {
    this.speakerMuted = muted;
    this.applySpeakerMute();
  }

  private ensurePlaybackGain(): GainNode | null {
    if (!this.captureContext) return null;
    if (!this.playbackGain) {
      this.playbackGain = this.captureContext.createGain();
      this.playbackGain.gain.value = this.speakerMuted ? 0 : 1;
      this.playbackGain.connect(this.captureContext.destination);
    }
    return this.playbackGain;
  }

  private applySpeakerMute(): void {
    const volume = this.speakerMuted ? 0 : 1;
    if (this.playbackGain) this.playbackGain.gain.value = volume;
    this.pcmPlayer?.setMuted(this.speakerMuted);
    if (this.streamAudio) {
      this.streamAudio.muted = this.speakerMuted;
      this.streamAudio.volume = volume;
    }
  }

  /** Agent assist backend_streaming: tell server which party is speaking on the mic. */
  setSpeaker(speaker: 'agent' | 'customer'): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(JSON.stringify({ type: 'set_speaker', speaker }));
    } catch {
      /* ignore */
    }
  }

  /** Browser-authoritative report of how much agent TTS was heard. */
  private notifyAgentPlayback(turn: number | null, playedMs: number, interrupted: boolean): void {
    if (this.playbackReportSent || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.playbackReportSent = true;
    try {
      this.ws.send(
        JSON.stringify({
          type: 'agent_playback',
          turn,
          played_ms: Math.max(0, Math.round(playedMs)),
          interrupted,
        })
      );
    } catch {
      /* ignore */
    }
  }

  private applyMediaConfig(payload: Record<string, unknown>): void {
    const audio = payload.audio;
    if (!audio || typeof audio !== 'object') return;
    const cfg = audio as Record<string, unknown>;
    this.bargeInConfig = {
      barge_in_enabled: cfg.barge_in_enabled !== false,
      barge_in_grace_ms:
        typeof cfg.barge_in_grace_ms === 'number' && Number.isFinite(cfg.barge_in_grace_ms)
          ? Math.max(0, Math.round(cfg.barge_in_grace_ms))
          : DEFAULT_MEDIA_BARGE_IN_CONFIG.barge_in_grace_ms,
      barge_in_rms_threshold:
        typeof cfg.barge_in_rms_threshold === 'number' && Number.isFinite(cfg.barge_in_rms_threshold)
          ? Math.max(0, cfg.barge_in_rms_threshold)
          : DEFAULT_MEDIA_BARGE_IN_CONFIG.barge_in_rms_threshold,
      barge_in_min_frames:
        typeof cfg.barge_in_min_frames === 'number' && Number.isFinite(cfg.barge_in_min_frames)
          ? Math.max(1, Math.round(cfg.barge_in_min_frames))
          : DEFAULT_MEDIA_BARGE_IN_CONFIG.barge_in_min_frames,
      barge_in_vad_enabled: cfg.barge_in_vad_enabled !== false,
      barge_in_vad_threshold:
        typeof cfg.barge_in_vad_threshold === 'number' && Number.isFinite(cfg.barge_in_vad_threshold)
          ? Math.min(1, Math.max(0, cfg.barge_in_vad_threshold))
          : DEFAULT_MEDIA_BARGE_IN_CONFIG.barge_in_vad_threshold,
      stt_gate_while_bot_speaking: cfg.stt_gate_while_bot_speaking !== false,
    };
    const sampleRate =
      typeof cfg.tts_sample_rate === 'number' && Number.isFinite(cfg.tts_sample_rate)
        ? Math.round(cfg.tts_sample_rate)
        : null;
    const preroll =
      typeof cfg.tts_preroll_ms === 'number' && Number.isFinite(cfg.tts_preroll_ms)
        ? Math.round(cfg.tts_preroll_ms)
        : DEFAULT_TTS_WIRE_CONFIG.tts_preroll_ms;
    this.ttsWire = {
      tts_playback: cfg.tts_playback === 'mp3' ? 'mp3' : 'pcm',
      tts_encoding: cfg.tts_encoding === 'pcm_s16le' ? 'pcm_s16le' : 'mp3',
      tts_sample_rate: sampleRate,
      tts_channels:
        typeof cfg.tts_channels === 'number' && Number.isFinite(cfg.tts_channels)
          ? Math.max(1, Math.round(cfg.tts_channels))
          : 1,
      tts_preroll_ms: preroll,
    };
    this.ensurePcmPlayer();
    if (this.pcmPlayer && this.ttsWire.tts_sample_rate) {
      this.pcmPlayer.setInputSampleRate(this.ttsWire.tts_sample_rate);
      this.pcmPlayer.setPrerollMs(this.ttsWire.tts_preroll_ms);
    }
    this.bargeVad.setThreshold(this.bargeInConfig.barge_in_vad_threshold);
    if (
      this.bargeVadAvailable &&
      this.bargeInConfig.barge_in_enabled &&
      this.bargeInConfig.barge_in_vad_enabled
    ) {
      this.bargeVad.start();
    }
  }

  private isBotSpeaking(): boolean {
    return (
      !this.ttsCancelled &&
      (this.receivingTts || this.streamAudio !== null || this.pcmPlayer?.isPlaying === true)
    );
  }

  private bargeElapsed(): number {
    return this.bargeCandidateAt > 0 ? Date.now() - this.bargeCandidateAt : 0;
  }

  private noteBargeCandidate(): void {
    if (this.bargeCandidateAt > 0) return;
    this.bargeCandidateAt = Date.now();
    console.log('[barge-in] candidate t=0');
  }

  private maybeBargeOnMicActivity(input: Float32Array, sampleRate: number): void {
    if (
      !this.bargeVadAvailable ||
      !this.bargeInConfig.barge_in_enabled ||
      !this.isBotSpeaking()
    ) {
      this.bargeLoudFrames = 0;
      this.bargeCandidateAt = 0;
      return;
    }
    if (
      this.botSpeechStartedAt > 0 &&
      Date.now() - this.botSpeechStartedAt < this.bargeInConfig.barge_in_grace_ms
    ) {
      this.bargeLoudFrames = 0;
      this.bargeCandidateAt = 0;
      return;
    }
    let sum = 0;
    for (let i = 0; i < input.length; i += 1) sum += input[i] * input[i];
    const rms = Math.sqrt(sum / Math.max(1, input.length));
    if (rms < this.bargeInConfig.barge_in_rms_threshold) {
      this.bargeLoudFrames = 0;
      this.bargeCandidateAt = 0;
      this.bargeVad.reset();
      return;
    }
    this.noteBargeCandidate();
    if (!this.bargeInConfig.barge_in_vad_enabled) {
      this.noteSpeechFrame();
      return;
    }
    this.enqueueVad(downsampleFloat32(input, sampleRate, MEDIA_PCM_SAMPLE_RATE));
  }

  private enqueueVad(frame: Float32Array): void {
    if (this.vadBusy) {
      this.vadPending = frame;
      console.log(
        `[barge-in] inference skipped vadPending t=${this.bargeElapsed()}ms loudFrames=${this.bargeLoudFrames}`
      );
      return;
    }
    this.vadBusy = true;
    void this.runVad(frame);
  }

  private async runVad(frame: Float32Array): Promise<void> {
    const started = Date.now();
    console.log(`[barge-in] inference start t=${this.bargeElapsed()}ms`);
    try {
      const speech = await this.bargeVad.isSpeech(frame);
      const dur = Date.now() - started;
      const elapsed = this.bargeElapsed();
      if (!this.bargeInConfig.barge_in_enabled || !this.isBotSpeaking()) {
        console.log(
          `[barge-in] inference end t=${elapsed}ms dur=${dur}ms result=${speech ? 'positive' : 'negative'} loudFrames=${this.bargeLoudFrames}`
        );
        this.bargeLoudFrames = 0;
        this.bargeCandidateAt = 0;
        return;
      }
      if (speech) this.noteSpeechFrame();
      else {
        this.bargeLoudFrames = 0;
        this.bargeCandidateAt = 0;
        this.bargeVad.reset();
      }
      console.log(
        `[barge-in] inference end t=${elapsed}ms dur=${dur}ms result=${speech ? 'positive' : 'negative'} loudFrames=${this.bargeLoudFrames}`
      );
    } finally {
      this.vadBusy = false;
      const next = this.vadPending;
      this.vadPending = null;
      if (next && this.isBotSpeaking()) this.enqueueVad(next);
    }
  }

  private noteSpeechFrame(): void {
    this.bargeLoudFrames += 1;
    if (this.bargeLoudFrames < this.bargeInConfig.barge_in_min_frames) return;
    const now = Date.now();
    if (now - this.lastBargeSentAt < BARGE_IN_MIC_DEBOUNCE_MS) return;
    console.log(
      `[barge-in] confirmed t=${this.bargeElapsed()}ms loudFrames=${this.bargeLoudFrames}`
    );
    this.bargeLoudFrames = 0;
    this.bargeCandidateAt = 0;
    this.bargeVad.reset();
    this.lastBargeSentAt = now;
    this.interruptBotSpeech();
  }

  private getStreamPlayedMs(): number {
    if (this.pcmPlayer && this.ttsWire.tts_encoding === 'pcm_s16le') {
      return this.pcmPlayer.getPlayedMs();
    }
    if (this.streamAudio && Number.isFinite(this.streamAudio.currentTime)) {
      return Math.max(0, this.streamAudio.currentTime * 1000);
    }
    return 0;
  }

  private resetStreamPlayback(): void {
    this.stopStreamPlayback();
    this.streamAppendQueue = [];
    this.streamSourceOpen = false;
    this.streamEndRequested = false;
    this.sourceBuffer = null;
    this.mediaSource = null;
  }

  private stopStreamPlayback(): void {
    if (this.streamAudio) {
      this.streamAudio.onended = null;
      try {
        this.streamAudio.pause();
      } catch {
        /* ignore */
      }
      this.streamAudio.removeAttribute('src');
      this.streamAudio.load();
    }
    if (this.streamObjectUrl) {
      URL.revokeObjectURL(this.streamObjectUrl);
      this.streamObjectUrl = null;
    }
    this.streamAudio = null;
  }

  private ensureStreamPlayback(turn: number | null): void {
    if (this.mediaSource || !this.captureContext) return;
    this.botTurn = turn;
    const ctx = this.captureContext;
    const audio = new Audio();
    audio.setAttribute('playsinline', 'true');
    const ms = new MediaSource();
    this.streamAudio = audio;
    this.mediaSource = ms;
    this.streamObjectUrl = URL.createObjectURL(ms);
    audio.src = this.streamObjectUrl;
    this.applySpeakerMute();

    if (!this.streamElementSource) {
      this.streamElementSource = ctx.createMediaElementSource(audio);
      this.streamElementSource.connect(this.ensurePlaybackGain() ?? ctx.destination);
    }

    ms.addEventListener(
      'sourceopen',
      () => {
        if (!this.mediaSource || this.mediaSource.readyState !== 'open') return;
        try {
          this.sourceBuffer = this.mediaSource.addSourceBuffer('audio/mpeg');
          this.sourceBuffer.mode = 'sequence';
          this.sourceBuffer.addEventListener('updateend', () => this.drainStreamAppendQueue());
          this.streamSourceOpen = true;
          this.drainStreamAppendQueue();
        } catch {
          this.callbacks.onError?.('Bot audio playback is not supported in this browser');
        }
      },
      { once: true }
    );

    void audio.play().catch(() => undefined);
  }

  private drainStreamAppendQueue(): void {
    if (!this.sourceBuffer || this.sourceBuffer.updating || !this.streamSourceOpen) {
      if (this.streamEndRequested) this.tryEndMediaStream();
      return;
    }
    const next = this.streamAppendQueue.shift();
    if (!next) {
      if (this.streamEndRequested) this.tryEndMediaStream();
      return;
    }
    try {
      this.sourceBuffer.appendBuffer(next);
    } catch {
      this.streamAppendQueue.unshift(next);
    }
  }

  private appendStreamChunk(chunk: Uint8Array): void {
    if (this.ttsCancelled || chunk.length === 0) return;
    if (this.blobPlayback) {
      this.streamAppendQueue.push(chunk);
      return;
    }
    this.ensureStreamPlayback(this.botTurn);
    this.streamAppendQueue.push(chunk);
    this.drainStreamAppendQueue();
  }

  private tryEndMediaStream(): void {
    if (!this.mediaSource || this.mediaSource.readyState !== 'open') return;
    if (this.sourceBuffer?.updating) return;
    if (this.streamAppendQueue.length > 0) return;
    try {
      this.mediaSource.endOfStream();
    } catch {
      /* ignore */
    }
  }

  private finalizeStreamPlayback(turn: number | null): void {
    if (this.blobPlayback) {
      this.playBufferedMp3(turn);
      return;
    }
    if (!this.streamAudio) {
      this.notifyAgentPlayback(turn, 0, false);
      this.finishTtsPlayback(turn);
      return;
    }
    this.streamEndRequested = true;
    this.drainStreamAppendQueue();
    this.streamAudio.onended = () => {
      const durationMs = Number.isFinite(this.streamAudio?.duration)
        ? Math.max(0, (this.streamAudio?.duration ?? 0) * 1000)
        : this.getStreamPlayedMs();
      this.notifyAgentPlayback(turn, durationMs, false);
      this.resetStreamPlayback();
      this.finishTtsPlayback(turn);
    };
  }

  /** Play one finished MP3 on browsers that cannot stream audio/mpeg (iPhone). */
  private playBufferedMp3(turn: number | null): void {
    const chunks = this.streamAppendQueue.splice(0);
    if (this.ttsCancelled || chunks.length === 0) {
      this.notifyAgentPlayback(turn, 0, this.ttsCancelled);
      this.finishTtsPlayback(turn);
      return;
    }
    const blob = new Blob(chunks, { type: 'audio/mpeg' });
    const url = URL.createObjectURL(blob);
    const audio = new Audio();
    audio.setAttribute('playsinline', 'true');
    audio.preload = 'auto';
    audio.src = url;
    this.streamAudio = audio;
    this.streamObjectUrl = url;
    this.applySpeakerMute();
    audio.onended = () => {
      const durationMs = Number.isFinite(audio.duration) ? Math.max(0, audio.duration * 1000) : 0;
      this.notifyAgentPlayback(turn, durationMs, false);
      this.resetStreamPlayback();
      this.finishTtsPlayback(turn);
    };
    audio.onerror = () => {
      this.callbacks.onError?.('Bot audio playback failed');
      this.notifyAgentPlayback(turn, 0, true);
      this.resetStreamPlayback();
      this.finishTtsPlayback(turn);
    };
    void audio.play().catch(() => {
      this.callbacks.onError?.('Tap the page to allow agent audio');
    });
  }

  connect(wsUrl: string): Promise<void> {
    this.closed = false;
    return warmSileroBargeVad().then((available) => {
      this.bargeVadAvailable = available;
      if (this.closed) return Promise.reject(new Error('WebSocket connection failed'));
      return new Promise<void>((resolve, reject) => {
        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        this.ws = ws;
        ws.onopen = () => resolve();
        ws.onerror = () => {
          if (ws.readyState !== WebSocket.OPEN) reject(new Error('WebSocket connection failed'));
        };
        ws.onclose = () => {
          this.ws = null;
          this.callbacks.onClose?.();
        };
        ws.onmessage = (ev) => {
          if (typeof ev.data === 'string') this.handleJsonMessage(ev.data);
          else if (ev.data instanceof ArrayBuffer) this.handleBinaryMessage(ev.data);
        };
      });
    });
  }

  private startMicCapture(): void {
    if (this.closed || this.micStarted || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (!this.captureContext || !this.micStream) {
      throw new Error('Microphone not initialized');
    }
    const track = this.micStream.getAudioTracks()[0];
    if (!track || track.readyState !== 'live') {
      throw new Error('Microphone track is not active');
    }
    this.micStarted = true;
    const ctx = this.captureContext;
    this.kickResume();
    const source = ctx.createMediaStreamSource(this.micStream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    this.processor = processor;
    processor.onaudioprocess = (event) => {
      if (this.closed || this.micMuted || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
        return;
      }
      const input = event.inputBuffer.getChannelData(0);
      this.maybeBargeOnMicActivity(input, ctx.sampleRate);
      const down = downsampleFloat32(input, ctx.sampleRate, MEDIA_PCM_SAMPLE_RATE);
      const pcm = float32ToInt16Pcm(down);
      if (pcm.byteLength === 0) return;
      const gateStt =
        this.bargeInConfig.stt_gate_while_bot_speaking && this.isBotSpeaking();
      if (gateStt) {
        this.pushSttPreroll(pcm);
        return;
      }
      this.flushSttPreroll();
      this.queuePcmSend(pcm);
    };
    const silent = ctx.createGain();
    silent.gain.value = 0;
    source.connect(processor);
    processor.connect(silent);
    silent.connect(ctx.destination);
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0;
    osc.connect(oscGain);
    oscGain.connect(ctx.destination);
    osc.start();
    this.keepAliveOsc = osc;
    this.pcmFlushTimer = setInterval(() => this.flushPcmSendBuffer(false), 100);
    ctx.onstatechange = () => {
      if (ctx.state === 'suspended' && this.micStarted && !this.closed) {
        this.kickResume();
      }
    };
  }

  private pushSttPreroll(pcm: ArrayBuffer): void {
    const bytes = new Uint8Array(pcm);
    if (bytes.length === 0) return;
    this.sttPreroll.push(bytes);
    this.sttPrerollBytes += bytes.length;
    while (this.sttPrerollBytes > STT_PREROLL_BYTES && this.sttPreroll.length > 1) {
      const dropped = this.sttPreroll.shift();
      if (dropped) this.sttPrerollBytes -= dropped.length;
    }
  }

  /** Send the whole gated tail, then drop the ring. */
  private flushSttPreroll(): void {
    const chunks = this.sttPreroll;
    this.sttPreroll = [];
    this.sttPrerollBytes = 0;
    for (const chunk of chunks) {
      const pcm = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
      this.queuePcmSend(pcm);
    }
  }

  private clearSttPreroll(): void {
    this.sttPreroll = [];
    this.sttPrerollBytes = 0;
  }

  private queuePcmSend(pcm: ArrayBuffer): void {
    const bytes = new Uint8Array(pcm);
    if (bytes.length === 0) return;
    const merged = new Uint8Array(this.pcmSendBuffer.length + bytes.length);
    merged.set(this.pcmSendBuffer);
    merged.set(bytes, this.pcmSendBuffer.length);
    this.pcmSendBuffer = merged;
    while (this.pcmSendBuffer.length >= MEDIA_PCM_BATCH_BYTES) {
      const chunk = this.pcmSendBuffer.slice(0, MEDIA_PCM_BATCH_BYTES);
      this.pcmSendBuffer = this.pcmSendBuffer.slice(MEDIA_PCM_BATCH_BYTES);
      const frame = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
      try {
        this.ws?.send(frame);
      } catch (err) {
        this.callbacks.onError?.(err instanceof Error ? err.message : 'Failed to send microphone audio');
        return;
      }
      if (!this.pcmSent) {
        this.pcmSent = true;
        this.callbacks.onPcmStreaming?.();
      }
    }
  }

  stop(options?: { closeAudio?: boolean }): void {
    const closeAudio = options?.closeAudio !== false;
    if (!this.playbackReportSent && (this.receivingTts || this.streamAudio || this.pcmPlayer?.isPlaying)) {
      this.notifyAgentPlayback(this.botTurn, this.getStreamPlayedMs(), true);
    }
    this.closed = true;
    this.flushPcmSendBuffer(true);
    this.stopMicCapture();
    this.pcmPlayer?.dispose();
    this.pcmPlayer = null;
    this.resetStreamPlayback();
    this.pendingBinary = [];
    this.receivingTts = false;
    this.ttsCancelled = false;
    this.pcmSent = false;
    this.playbackReportSent = false;
    this.botTurn = null;
    this.unbindResumeRecovery?.();
    this.unbindResumeRecovery = null;
    try {
      if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ type: 'stop' }));
    } catch {
      /* ignore */
    }
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
    if (closeAudio) {
      this.micStream?.getTracks().forEach((t) => t.stop());
      if (this.captureContext) {
        this.captureContext.onstatechange = null;
        this.captureContext.close().catch(() => undefined);
      }
    }
    this.micStream = null;
    this.captureContext = null;
    this.streamElementSource = null;
    this.playbackGain = null;
    this.pcmSendBuffer = new Uint8Array(0);
    this.clearSttPreroll();
  }

  private flushPcmSendBuffer(forceMin: boolean): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (forceMin) this.pcmSendBuffer = new Uint8Array(0);
      return;
    }
    while (this.pcmSendBuffer.length >= MEDIA_PCM_BATCH_BYTES) {
      const chunk = this.pcmSendBuffer.slice(0, MEDIA_PCM_BATCH_BYTES);
      this.pcmSendBuffer = this.pcmSendBuffer.slice(MEDIA_PCM_BATCH_BYTES);
      const frame = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
      try {
        this.ws.send(frame);
      } catch (err) {
        this.callbacks.onError?.(err instanceof Error ? err.message : 'Failed to send microphone audio');
        return;
      }
      if (!this.pcmSent) {
        this.pcmSent = true;
        this.callbacks.onPcmStreaming?.();
      }
    }
    if (forceMin && this.pcmSendBuffer.length >= MEDIA_PCM_MIN_BYTES) {
      const frame = this.pcmSendBuffer.buffer.slice(
        this.pcmSendBuffer.byteOffset,
        this.pcmSendBuffer.byteOffset + this.pcmSendBuffer.byteLength
      );
      try {
        this.ws.send(frame);
      } catch (err) {
        this.callbacks.onError?.(err instanceof Error ? err.message : 'Failed to send microphone audio');
      }
      if (!this.pcmSent) {
        this.pcmSent = true;
        this.callbacks.onPcmStreaming?.();
      }
      this.pcmSendBuffer = new Uint8Array(0);
    }
  }

  private stopMicCapture(): void {
    if (this.pcmFlushTimer != null) {
      clearInterval(this.pcmFlushTimer);
      this.pcmFlushTimer = null;
    }
    try {
      this.keepAliveOsc?.stop();
    } catch {
      /* ignore */
    }
    this.keepAliveOsc = null;
    this.processor?.disconnect();
    this.processor = null;
    this.micStarted = false;
  }

  private interruptBotSpeech(): void {
    if (this.ttsCancelled && this.playbackReportSent) return;
    const turn = this.botTurn;
    const playedMs = this.getStreamPlayedMs();
    this.lastBargeSentAt = Date.now();
    this.notifyAgentPlayback(turn, playedMs, true);
    this.ttsCancelled = true;
    if (this.ttsWire.tts_encoding === 'pcm_s16le') {
      this.pcmPlayer?.clear();
    } else {
      this.resetStreamPlayback();
    }
    this.pendingBinary = [];
    this.receivingTts = false;
  }

  private handleJsonMessage(raw: string): void {
    let payload: Record<string, unknown>;
    try {
      payload = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }
    switch (String(payload.type ?? '')) {
      case 'media_ready':
        this.applyMediaConfig(payload);
        try {
          this.startMicCapture();
          this.callbacks.onReady?.();
        } catch (err) {
          this.callbacks.onError?.(err instanceof Error ? err.message : 'Microphone failed to start');
        }
        break;
      case 'partial_stt':
        this.callbacks.onPartialStt?.(String(payload.text ?? ''));
        break;
      case 'stt_final': {
        const speaker = String(payload.speaker ?? 'customer');
        this.callbacks.onSttFinal?.(String(payload.text ?? ''), speaker);
        this.callbacks.onPartialStt?.('');
        break;
      }
      case 'bot_speech': {
        const turn = typeof payload.turn === 'number' ? payload.turn : null;
        if (
          this.isBotSpeaking() &&
          this.botTurn !== null &&
          turn !== null &&
          turn !== this.botTurn &&
          !this.playbackReportSent
        ) {
          this.notifyAgentPlayback(this.botTurn, this.getStreamPlayedMs(), true);
        }
        this.ttsCancelled = false;
        this.playbackReportSent = false;
        this.botSpeechStartedAt = Date.now();
        this.bargeLoudFrames = 0;
        this.bargeCandidateAt = 0;
        this.bargeVad.reset();
        this.clearSttPreroll();
        this.pcmTurnAborted = false;
        if (this.ttsWire.tts_encoding === 'pcm_s16le') {
          this.ensurePcmPlayer();
          this.pcmPlayer?.beginUtterance();
        } else {
          this.resetStreamPlayback();
        }
        this.botTurn = turn;
        this.receivingTts = true;
        if (this.pendingBinary.length > 0) {
          for (const chunk of this.pendingBinary) {
            if (this.ttsWire.tts_encoding === 'pcm_s16le') this.pcmPlayer?.pushPcm16(chunk);
            else this.appendStreamChunk(chunk);
          }
          this.pendingBinary = [];
        }
        this.callbacks.onBotSpeech?.(turn, String(payload.text ?? ''));
        break;
      }
      case 'tts_end': {
        const turn = typeof payload.turn === 'number' ? payload.turn : this.botTurn;
        if (turn !== null && this.botTurn !== null && turn !== this.botTurn) {
          break;
        }
        this.receivingTts = false;
        if (this.ttsWire.tts_encoding === 'pcm_s16le') {
          if (this.ttsCancelled || payload.cancelled === true || this.pcmTurnAborted) {
            if (!this.playbackReportSent) {
              this.notifyAgentPlayback(turn, this.getStreamPlayedMs(), true);
            }
            this.ttsCancelled = false;
            if (!this.pcmTurnAborted) this.pcmPlayer?.clear();
            this.pendingBinary = [];
            this.finishTtsPlayback(turn);
            break;
          }
          this.pcmPlayer?.flush();
          break;
        }
        if (this.ttsCancelled || payload.cancelled === true) {
          if (!this.playbackReportSent) {
            this.notifyAgentPlayback(turn, this.getStreamPlayedMs(), true);
          }
          this.ttsCancelled = false;
          this.resetStreamPlayback();
          this.pendingBinary = [];
          this.finishTtsPlayback(turn);
          break;
        }
        this.finalizeStreamPlayback(turn);
        break;
      }
      case 'turn_complete':
        if (typeof payload.turn === 'number' && payload.result && typeof payload.result === 'object') {
          this.callbacks.onTurnComplete?.({
            turn: payload.turn,
            speaker: String(payload.speaker ?? 'customer'),
            utterance: String(payload.utterance ?? ''),
            result: payload.result as Record<string, unknown>,
          });
        }
        break;
      case 'error':
        this.callbacks.onError?.(String(payload.message ?? 'Media stream error'));
        break;
      case 'stt_status': {
        const state = String(payload.state ?? '');
        this.callbacks.onSttStatus?.(state, payload);
        console.debug('[media] stt_status', state, payload);
        break;
      }
      default:
        break;
    }
  }

  private handleBinaryMessage(data: ArrayBuffer): void {
    if (data.byteLength === 0) return;
    if (this.ttsCancelled) return;
    const chunk = new Uint8Array(data);
    if (this.ttsWire.tts_encoding === 'pcm_s16le') {
      if (this.pcmTurnAborted) return;
      if (this.receivingTts) {
        this.ensurePcmPlayer();
        this.pcmPlayer?.pushPcm16(chunk);
      } else {
        this.pendingBinary.push(chunk);
      }
      return;
    }
    if (this.receivingTts) {
      this.appendStreamChunk(chunk);
    } else {
      this.pendingBinary.push(chunk);
    }
  }

  private onPcmPlaybackEnded(stats: PcmPlaybackStats, interrupted: boolean): void {
    if (!this.playbackReportSent) {
      this.notifyAgentPlayback(this.botTurn, stats.playedMs, interrupted);
    }
    this.sendPlaybackMetrics(stats);
    this.finishTtsPlayback(this.botTurn);
  }

  private onPcmFatalOverflow(stats: PcmPlaybackStats): void {
    this.pcmTurnAborted = true;
    this.ttsCancelled = true;
    this.receivingTts = false;
    this.pendingBinary = [];
    this.onPcmPlaybackEnded(stats, true);
    this.callbacks.onError?.('Agent audio buffer overflowed; utterance stopped');
  }

  private sendPlaybackMetrics(stats: PcmPlaybackStats): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    try {
      this.ws.send(
        JSON.stringify({
          type: 'tts_playback',
          turn: this.botTurn,
          tts_first_byte_received_ms: stats.ttsFirstByteReceivedMs,
          audible_playback_started_ms: stats.audiblePlaybackStartedMs,
          playback_finished_ms: stats.playbackFinishedMs,
          buffer_depth_samples: stats.bufferDepthSamples,
          preroll_samples: stats.prerollSamples,
          underrun_count: stats.underrunCount,
          underrun_duration_ms: stats.underrunDurationMs,
          overflow_count: stats.overflowCount,
          fatal_overflow: stats.fatalOverflow,
          played_ms: stats.playedMs,
        })
      );
    } catch {
      /* ignore */
    }
  }

  private finishTtsPlayback(turn: number | null): void {
    this.botSpeechStartedAt = 0;
    this.callbacks.onTtsEnd?.(turn);
  }
}
